import classNames from 'classnames';
import { m } from 'motion/react';
import { type FC, type PropsWithChildren, type ReactElement } from 'react';
import { createPortal } from 'react-dom';

import Info from '@/components/Icons/Info';
import usePopover from '@/hooks/usePopover';

import s from './Tooltip.module.scss';

const Tooltip: FC<
  PropsWithChildren<{
    text: string | ReactElement | null;
    id?: string;
    alignment?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'top-center' | 'bottom-center';
    classNameWrapper?: string;
    classNameTooltip?: string;
    ignoreKeyboardNavigation?: boolean;
  }>
> = ({ text, id, alignment, children, classNameWrapper, classNameTooltip, ignoreKeyboardNavigation }) => {
  const { isOpen, setIsOpen, menuRef, triggerRef } = usePopover(alignment || 'bottom-center');
  const isTopAligned = alignment === 'top-left' || alignment === 'top-right' || alignment === 'top-center';
  const cls = classNames(
    alignment === 'bottom-left' && s.tooltipLeft,
    alignment === 'bottom-right' && s.tooltipRight,
    alignment === 'top-right' && s.tooltipTopRight,
    alignment === 'top-left' && s.tooltipTopLeft,
    alignment === 'top-center' && s.tooltipTop,
    (!alignment || alignment === 'bottom-center') && s.tooltip,
    classNameTooltip,
  );
  return (
    <div
      className={classNames(s.tooltipWrapper, classNameWrapper)}
      style={{
        alignItems:
          (alignment === 'bottom-left' && 'flex-start') || (alignment === 'bottom-right' && 'flex-end') || 'center',
      }}
    >
      <div
        onClick={() => setIsOpen(!isOpen)}
        onFocus={ignoreKeyboardNavigation ? undefined : () => setIsOpen(true)}
        onBlur={() => setIsOpen(false)}
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
        className={s.trigger}
        ref={triggerRef as unknown as React.RefObject<HTMLDivElement>}
        {...(!children && {
          style: {
            height: 44,
            width: 44,
          },
        })}
      >
        {children || <Info weight='fill' size={20} />}
      </div>
      {text !== null &&
        isOpen &&
        createPortal(
          <m.div
            role='tooltip'
            ref={menuRef}
            id={(id && `${id}-describer`) || undefined}
            initial={{ opacity: 0, y: 24 }}
            // moves the tooltip up or down to create a 4px gap between the arrow tip and the element
            animate={{ opacity: 1, y: isTopAligned ? -8 : 8 }}
            className={cls}
          >
            {text}
          </m.div>,
          document.body,
        )}
    </div>
  );
};

export default Tooltip;
